import { ApolloError } from '@apollo/client';

import {
  usePandaDocAnalyticsQuery,
  PandaDocAnalyticsQuery,
} from 'core/graphql';
import { isPermissionError as isPermissionDeniedError } from 'core/utils/isPermissionError';

export type PandaDocAnalyticsData = NonNullable<
  PandaDocAnalyticsQuery['pandadocAnalytics']
>;

/**
 * True when the backend rejected the request because of ERP-scope roles — the
 * app-wide check (`core/utils/isPermissionError`), re-exported under the old
 * name for the PandaDoc hooks that import it from here.
 */
export { isPermissionDeniedError };

export interface IPandaDocAnalyticsArgs {
  granularity: string;
  course: string;
  /** Narrows tts KPIs/histogram to one document type (student|trainer|dofin). */
  docType?: string;
  /** ISO `YYYY-MM-DD`; `null`/omitted means no lower bound. */
  dateFrom?: string | null;
  dateTo?: string | null;
  /**
   * ТЗ 0078: чьи документы считать — `pandadoc` | `esign` (договоры ERP) |
   * `all`. Пусто — как решит бэкенд (только PandaDoc).
   */
  source?: string | null;
  /** ТЗ 0083: продавец / кто отправил — только договоры ERP. */
  sellerId?: string | null;
  sentById?: string | null;
}

interface IPandaDocAnalyticsHook {
  data?: PandaDocAnalyticsData;
  loading: boolean;
  /** Stays true on every refetch so the page can dim stale data. */
  fetching: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  refetch: () => void;
}

/**
 * Loads the pre-computed PandaDoc analytics for the current filters.
 * The backend does all aggregation — a filter change is just a re-query.
 */
export const usePandaDocAnalytics = ({
  granularity,
  course,
  docType,
  dateFrom,
  dateTo,
  source,
  sellerId,
  sentById,
}: IPandaDocAnalyticsArgs): IPandaDocAnalyticsHook => {
  const {
    data,
    loading,
    error,
    refetch,
    networkStatus,
  } = usePandaDocAnalyticsQuery({
    variables: {
      granularity,
      course,
      docType: docType || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      source: source || undefined,
      sellerId: sellerId || undefined,
      sentById: sentById || undefined,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    data: data?.pandadocAnalytics ?? undefined,
    loading,
    // networkStatus 1 = initial loading, 4 = refetch, 2 = setVariables.
    fetching: loading || networkStatus === 4 || networkStatus === 2,
    error,
    permissionDenied: isPermissionDeniedError(error),
    refetch: () => {
      refetch();
    },
  };
};
